import React, { useState, useEffect } from 'react';
import { Check, Zap, Crown, Rocket, Search, FileText, TrendingUp, Users, MessageSquare, Link, Lightbulb, BarChart, Menu, X, LogOut } from 'lucide-react';

export default function AffiliatIQ() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('niche');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    const storedUser = sessionStorage.getItem('affiliateUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setCurrentPage('app');
    }
    
    if (window.location.pathname === '/success') {
      setCurrentPage('success');
    }

    if (!document.getElementById('stripe-js')) {
      const script = document.createElement('script');
      script.id = 'stripe-js';
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const tools = [
    { id: 'niche', name: 'Niche Research', icon: Search, desc: 'Find profitable niches' },
    { id: 'content', name: 'Content Ideas', icon: FileText, desc: 'Generate content topics' },
    { id: 'strategy', name: 'Strategy Plan', icon: TrendingUp, desc: 'Build marketing strategy' },
    { id: 'audience', name: 'Audience Analysis', icon: Users, desc: 'Understand your audience' },
    { id: 'copy', name: 'Copy Generator', icon: MessageSquare, desc: 'Write promotional copy' },
    { id: 'keywords', name: 'SEO Keywords', icon: Link, desc: 'Find SEO keywords' },
    { id: 'product', name: 'Product Analysis', icon: Lightbulb, desc: 'Evaluate products' },
    { id: 'metrics', name: 'Metrics Tracker', icon: BarChart, desc: 'Track performance' },
  ];

  const getEnvVar = (key, defaultValue = '') => {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key];
    }
    return defaultValue;
  };

  const pricingPlans = [
    {
      name: 'Starter',
      price: 29,
      yearlyPrice: 23,
      icon: Zap,
      description: 'Perfect for beginners',
      monthlyPriceId: getEnvVar('REACT_APP_STARTER_MONTHLY_PRICE'),
      yearlyPriceId: getEnvVar('REACT_APP_STARTER_YEARLY_PRICE'),
      features: [
        '100 AI queries per month',
        'All 8 marketing tools',
        'Basic niche research',
        'Content idea generator',
        'Email support',
        'Export results as PDF'
      ],
      color: 'from-blue-500 to-blue-600',
      popular: false,
    },
    {
      name: 'Professional',
      price: 99,
      yearlyPrice: 79,
      icon: Crown,
      description: 'Best for growing affiliates',
      monthlyPriceId: getEnvVar('REACT_APP_PRO_MONTHLY_PRICE'),
      yearlyPriceId: getEnvVar('REACT_APP_PRO_YEARLY_PRICE'),
      features: [
        'Unlimited AI queries',
        'All 8 marketing tools',
        'Advanced analytics',
        'Priority support',
        'Campaign tracking',
        'Team collaboration (3 seats)',
        'White-label reports',
        'API access'
      ],
      color: 'from-purple-500 to-purple-600',
      popular: true,
    },
    {
      name: 'Business',
      price: 249,
      yearlyPrice: 199,
      icon: Rocket,
      description: 'For agencies & teams',
      monthlyPriceId: getEnvVar('REACT_APP_BUSINESS_MONTHLY_PRICE'),
      yearlyPriceId: getEnvVar('REACT_APP_BUSINESS_YEARLY_PRICE'),
      features: [
        'Everything in Professional',
        'Unlimited team seats',
        'Custom AI training',
        'Dedicated account manager',
        '1-on-1 strategy calls',
        'Custom integrations',
        'SLA guarantee',
        'Early access to features'
      ],
      color: 'from-pink-500 to-pink-600',
      popular: false,
    }
  ];

  const handleStripeCheckout = async (plan, cycle = 'monthly') => {
    try {
      const priceId = cycle === 'monthly' ? plan.monthlyPriceId : plan.yearlyPriceId;
      const stripeKey = getEnvVar('REACT_APP_STRIPE_PUBLISHABLE_KEY');

      console.log('Stripe Key:', stripeKey ? stripeKey.substring(0, 20) + '...' : 'NOT FOUND');
      console.log('Price ID:', priceId || 'NOT FOUND');

      if (!stripeKey || stripeKey === '' || !stripeKey.startsWith('pk_')) {
        alert('Stripe configuration error: Missing or invalid publishable key.\n\nPlease add REACT_APP_STRIPE_PUBLISHABLE_KEY to Vercel environment variables.');
        console.error('Stripe key missing or invalid');
        return;
      }

      if (!priceId || priceId === '') {
        alert('Stripe configuration error: Missing price ID.\n\nPlease add price IDs to Vercel environment variables.');
        console.error('Price ID missing');
        return;
      }

      if (!window.Stripe) {
        alert('Stripe.js is still loading. Please wait a moment and try again.');
        return;
      }

      const stripe = window.Stripe(stripeKey);
      
      if (!stripe) {
        alert('Failed to initialize Stripe. Please refresh and try again.');
        return;
      }

      console.log('Redirecting to Stripe Checkout...');

      const { error } = await stripe.redirectToCheckout({
        lineItems: [{ price: priceId, quantity: 1 }],
        mode: 'subscription',
        successUrl: `${window.location.origin}/success`,
        cancelUrl: `${window.location.origin}/pricing`,
      });

      if (error) {
        console.error('Stripe error:', error);
        alert('Payment error: ' + error.message);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('An error occurred. Please try again or contact support.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('affiliateUser');
    setUser(null);
    setCurrentPage('landing');
  };

  const handleGenerate = async () => {
    if (!input.trim()) return;
    
    setLoading(true);
    setOutput('');

    try {
      const prompts = {
        niche: `Analyze the following niche or industry for affiliate marketing potential: "${input}". Provide:
1. Market size and growth potential
2. Competition level (low/medium/high)
3. Average commission rates
4. Top affiliate programs in this niche
5. Target audience demographics
6. Content opportunities
7. Profitability rating (1-10)
8. Your recommendation`,

        content: `Generate 10 content ideas for affiliate marketing in this niche/topic: "${input}". For each idea include:
- Content title
- Content type (blog post, video, social media, email)
- Key points to cover
- Affiliate angle (how to naturally integrate affiliate links)`,

        strategy: `Create a 90-day affiliate marketing strategy for: "${input}". Include:
1. Month 1 objectives and tasks
2. Month 2 objectives and tasks
3. Month 3 objectives and tasks
4. Content calendar outline
5. Traffic generation methods
6. Conversion optimization tactics
7. Key metrics to track`,

        audience: `Analyze the target audience for this niche: "${input}". Provide:
1. Demographics (age, gender, income, location)
2. Psychographics (interests, values, lifestyle)
3. Pain points and problems
4. Goals and aspirations
5. Online behavior (where they hang out)
6. What messaging resonates
7. Objections to buying`,

        copy: `Write persuasive affiliate marketing copy for: "${input}". Create:
1. Attention-grabbing headline
2. Opening hook (2-3 sentences)
3. Main body (addressing pain points and benefits)
4. Social proof section
5. Call-to-action
6. P.S. line
Make it conversational and authentic.`,

        keywords: `Generate SEO keyword strategy for: "${input}". Provide:
1. 5 primary keywords (high volume, medium competition)
2. 10 long-tail keywords (buyer intent)
3. 5 question-based keywords
4. Search intent analysis for each keyword type
5. Content structure recommendations`,

        product: `Analyze this product/service for affiliate promotion: "${input}". Evaluate:
1. Product quality and reputation
2. Commission structure
3. Cookie duration
4. Conversion rate potential
5. Target audience fit
6. Competition level
7. Unique selling points
8. Promotion strategies
9. Red flags (if any)
10. Overall recommendation (Yes/No/Maybe)`,

        metrics: `Create a performance tracking framework for: "${input}". Include:
1. Key Performance Indicators (KPIs) to track
2. Daily metrics to monitor
3. Weekly metrics to review
4. Monthly metrics to analyze
5. Tools needed for tracking
6. Benchmarks for success
7. How to interpret the data
8. When to pivot strategy`,
      };

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [
            {
              role: 'user',
              content: prompts[activeTab]
            }
          ],
        }),
      });

      const data = await response.json();
      const result = data.content
        .filter(item => item.type === 'text')
        .map(item => item.text)
        .join('\n');
      
      setOutput(result);
    } catch (error) {
      setOutput('Error generating response. Please try again.');
      console.error('API Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (currentPage === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to AffiliatIQ! 🎉
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Your payment was successful! Check your email for login details and next steps.
          </p>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-3">What happens next?</h3>
            <ul className="text-left space-y-2 text-gray-700">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>You'll receive a welcome email with your login credentials</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>Access all 8 AI-powered marketing tools immediately</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>Start researching niches and generating content</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>Get priority email support if you need help</span>
              </li>
            </ul>
          </div>
          <button
            onClick={() => setCurrentPage('app')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:shadow-lg transform hover:scale-105 transition"
          >
            Start Using AffiliatIQ
          </button>
          <p className="mt-6 text-sm text-gray-500">
            Questions? Email us at support@affiliatiq.com
          </p>
        </div>
      </div>
    );
  }

  if (currentPage === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <Rocket className="w-8 h-8 text-purple-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">AffiliatIQ</span>
              </div>
              <div className="hidden md:flex space-x-8">
                <a href="#features" className="text-gray-700 hover:text-purple-600">Features</a>
                <a href="#pricing" className="text-gray-700 hover:text-purple-600">Pricing</a>
                <button 
                  onClick={() => setCurrentPage('pricing')}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition"
                >
                  Get Started
                </button>
              </div>
              <button 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Grow Your Affiliate Business
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                10x Faster with AI
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The most affordable AI-powered affiliate marketing assistant. Research niches, generate content, and scale your income—all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setCurrentPage('pricing')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:shadow-lg transform hover:scale-105 transition"
              >
                Start Free Trial
              </button>
              <button className="bg-white text-gray-700 px-8 py-4 rounded-lg text-lg font-semibold border-2 border-gray-300 hover:border-purple-600 transition">
                Watch Demo
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              20% cheaper than competitors • No credit card required • Cancel anytime
            </p>
          </div>
        </div>

        <div id="features" className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              Everything You Need to Succeed
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {tools.map(tool => {
                const Icon = tool.icon;
                return (
                  <div key={tool.id} className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-600 hover:shadow-lg transition">
                    <Icon className="w-12 h-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tool.name}</h3>
                    <p className="text-gray-600">{tool.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Affiliate Business?
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Join hundreds of affiliates already earning more with AI
            </p>
            <button 
              onClick={() => setCurrentPage('pricing')}
              className="bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:shadow-lg transform hover:scale-105 transition"
            >
              View Pricing Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentPage === 'pricing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => setCurrentPage('landing')}
            className="mb-8 text-purple-600 hover:text-purple-700 font-semibold"
          >
            ← Back to Home
          </button>

          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              20% cheaper than competitors. Start free, scale as you grow.
            </p>

            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-md">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full transition ${
                  billingCycle === 'monthly'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full transition ${
                  billingCycle === 'yearly'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {pricingPlans.map((plan) => {
              const Icon = plan.icon;
              const displayPrice = billingCycle === 'monthly' ? plan.price : plan.yearlyPrice;
              
              return (
                <div
                  key={plan.name}
                  className={`bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition ${
                    plan.popular ? 'ring-4 ring-purple-600' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-center py-2 font-semibold">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${plan.color} mb-4`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-gray-900">${displayPrice}</span>
                      <span className="text-gray-600">/month</span>
                      {billingCycle === 'yearly' && (
                        <div className="text-sm text-green-600 font-semibold mt-1">
                          ${displayPrice * 12} billed annually
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleStripeCheckout(plan, billingCycle)}
                      className={`w-full py-4 rounded-lg font-semibold text-lg transition ${
                        plan.popular
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-lg'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Get Started
                    </button>

                    <ul className="mt-8 space-y-4">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">How does the free trial work?</h3>
                <p className="text-gray-600">Start with 10 free queries. No credit card required. Upgrade anytime to unlock unlimited access.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600">Yes! Cancel with one click from your account. No questions asked, no cancellation fees.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Do you offer refunds?</h3>
                <p className="text-gray-600">We offer a 30-day money-back guarantee. If you're not satisfied, we'll refund you in full.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">What payment methods do you accept?</h3>
                <p className="text-gray-600">We accept all major credit cards via Stripe. Your payment information is secure and encrypted.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeTool = tools.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Rocket className="w-8 h-8 text-purple-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">AffiliatIQ</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user?.plan} Plan
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-purple-600"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
            <h1 className="text-3xl font-bold mb-2">Affiliate Marketing Assistant</h1>
            <p className="text-purple-100">AI-powered tools to grow your affiliate business</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 bg-gray-50">
            {tools.map(tool => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  onClick={() => {
                    setActiveTab(tool.id);
                    setOutput('');
                  }}
                  className={`p-4 rounded-xl transition-all ${
                    activeTab === tool.id
                      ? 'bg-purple-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <Icon className="w-6 h-6 mx-auto mb-2" />
                  <div className="text-sm font-semibold">{tool.name}</div>
                  <div className="text-xs mt-1 opacity-75">{tool.desc}</div>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {activeTool.name}
              </h2>
              <p className="text-gray-600">{activeTool.desc}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {activeTab === 'niche' && 'Enter niche or industry (e.g., "fitness for busy professionals")'}
                {activeTab === 'content' && 'Enter your niche or topic (e.g., "vegan cooking")'}
                {activeTab === 'strategy' && 'Enter your niche or product (e.g., "meditation apps")'}
                {activeTab === 'audience' && 'Enter your niche (e.g., "home automation")'}
                {activeTab === 'copy' && 'Enter product/offer (e.g., "Shopify course for beginners")'}
                {activeTab === 'keywords' && 'Enter main topic (e.g., "best web hosting")'}
                {activeTab === 'product' && 'Enter product/service name (e.g., "ConvertKit email service")'}
                {activeTab === 'metrics' && 'Enter your campaign or niche (e.g., "tech review blog")'}
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter your information here..."
                className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                rows="3"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || !input.trim()}
              className={`w-full py-4 rounded-lg font-semibold text-lg transition-all ${
                loading || !input.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-lg hover:scale-105'
              }`}
            >
              {loading ? 'Generating...' : 'Generate AI Insights'}
            </button>

            {output && (
              <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center">
                  <Lightbulb className="w-5 h-5 mr-2" />
                  AI Generated Insights
                </h3>
                <div className="prose prose-purple max-w-none">
                  <pre className="whitespace-pre-wrap text-gray-800 font-sans text-sm leading-relaxed">
                    {output}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
